import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Envelope, EnvelopeOpen, PaperPlaneTilt, Archive, ArrowsClockwise, CircleNotch,
  CaretDown, CaretUp, MagnifyingGlass, X as XIcon,
} from "@phosphor-icons/react";
import { fetchContactMessages, updateContactMessageStatus } from "@/lib/admin";
import Pagination, { paginate, DEFAULT_PAGE_SIZE } from "@/components/admin/Pagination";
import Highlight from "@/components/admin/Highlight";
import { searchItems, sortItems } from "@/lib/adminTableUtils";

// Sort options exposed as a dropdown — the messages view is an
// expandable list (not a traditional table) so a dropdown is clearer
// than row-level sortable headers.
const SORT_OPTIONS = [
  { value: "created_at:desc", label: "Newest first",   field: "created_at", direction: "desc" },
  { value: "created_at:asc",  label: "Oldest first",   field: "created_at", direction: "asc"  },
  { value: "name:asc",        label: "Name A → Z",     field: "name",       direction: "asc"  },
  { value: "name:desc",       label: "Name Z → A",     field: "name",       direction: "desc" },
  { value: "subject:asc",     label: "Subject A → Z",  field: "subject",    direction: "asc"  },
  { value: "status:asc",      label: "Status",         field: "status",     direction: "asc"  },
];

const STATUS_META = {
  new:      { label: "New",      className: "bg-[#FF2A00] text-white",       icon: Envelope },
  read:     { label: "Read",     className: "bg-neutral-100 text-neutral-700 border border-black/10", icon: EnvelopeOpen },
  replied:  { label: "Replied",  className: "bg-[#2400FF] text-white",       icon: PaperPlaneTilt },
  archived: { label: "Archived", className: "bg-neutral-200 text-neutral-500", icon: Archive },
};

function fmt(dt) {
  if (!dt) return "-";
  const d = new Date(dt);
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminMessagesTab() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all"); // all | new | read | replied | archived
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Pagination state — resets to page 1 whenever the filter or dataset
  // changes so users don't land on an empty page after narrowing.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Search across name / email / subject / category / message. Sort
  // dropdown drives created_at / name / subject / status ordering.
  const [searchQuery, setSearchQuery] = useState("");
  const [sortValue, setSortValue] = useState("created_at:desc");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await fetchContactMessages();
      setRows(data.messages || []);
      setStats(data.stats || { total: 0, new: 0, read: 0, replied: 0 });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const sortCfg = SORT_OPTIONS.find((o) => o.value === sortValue);
    // Normalise status so `null`/missing values still sort together.
    const normalised = rows.map((r) => ({ ...r, status: r.status || "new" }));
    const byStatus = filter === "all" ? normalised : normalised.filter((r) => r.status === filter);
    const searched = searchItems(byStatus, searchQuery, [
      "name", "email", "subject", "category", "message",
    ]);
    return sortItems(searched, sortCfg);
  }, [rows, filter, searchQuery, sortValue]);
  // Visible slice after pagination.
  const visible = useMemo(() => paginate(filtered, page, pageSize), [filtered, page, pageSize]);
  // Reset to page 1 whenever the filter / search / sort changes or the
  // underlying rows refresh — otherwise `page=3` becomes invalid when
  // the list shrinks.
  useEffect(() => { setPage(1); }, [filter, searchQuery, sortValue, rows.length]);

  const changeStatus = async (id, status) => {
    setBusyId(id);
    try {
      await updateContactMessageStatus(id, status);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      // Refresh stats locally
      setStats((s) => {
        const next = { ...s };
        const current = rows.find((r) => r.id === id)?.status || "new";
        if (next[current] > 0) next[current] -= 1;
        next[status] = (next[status] || 0) + 1;
        return next;
      });
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = async (row) => {
    const nextId = expandedId === row.id ? null : row.id;
    setExpandedId(nextId);
    // Auto-mark New → Read on first open (nice-to-have triage nudge).
    if (nextId && (row.status || "new") === "new") {
      changeStatus(row.id, "read");
    }
  };

  const replyLink = (row) => {
    const subject = encodeURIComponent(`Re: ${row.subject || "your message"}`);
    const body = encodeURIComponent(
      `Hi ${row.name || "there"},\n\nThanks for reaching out.\n\n---\nYour message:\n${row.message}\n`
    );
    return `mailto:${row.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="admin-messages-tab">
      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="messages-stats">
        <StatChip label="Total"   value={stats.total   ?? 0} testid="msg-stat-total" />
        <StatChip label="New"     value={stats.new     ?? 0} testid="msg-stat-new" accent="#FF2A00" />
        <StatChip label="Read"    value={stats.read    ?? 0} testid="msg-stat-read" />
        <StatChip label="Replied" value={stats.replied ?? 0} testid="msg-stat-replied" accent="#2400FF" />
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="messages-filters">
        {["all", "new", "read", "replied", "archived"].map((s) => {
          const isActive = filter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${
                isActive ? "bg-neutral-950 text-white border-neutral-950" : "border-black/15 text-neutral-700 hover:bg-neutral-100"
              }`}
              data-testid={`messages-filter-${s}`}
            >
              {s === "all" ? `All (${stats.total ?? 0})` : `${s} (${stats[s] ?? 0})`}
            </button>
          );
        })}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-2.5 py-1.5 border border-black/15 text-neutral-700 hover:bg-neutral-100"
          data-testid="messages-refresh"
        >
          {loading ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <ArrowsClockwise size={12} weight="bold" />}
          Refresh
        </button>
      </div>

      {/* Search + sort toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="messages-toolbar">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, email, subject, message…"
            className="field-input w-full pl-9 pr-9"
            data-testid="messages-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center text-neutral-400 hover:text-neutral-900"
              data-testid="messages-search-clear"
              aria-label="Clear search"
            >
              <XIcon size={12} weight="bold" />
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-neutral-500">
          Sort
          <select
            value={sortValue}
            onChange={(e) => setSortValue(e.target.value)}
            className="field-input py-2 pr-6 text-xs"
            data-testid="messages-sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 ml-auto" data-testid="messages-search-count">
          {filtered.length} shown
        </div>
      </div>

      {/* Error / empty / list */}
      {err && (
        <div className="border border-[#FF2A00]/30 bg-[#FF2A00]/[0.05] p-3 text-sm text-[#FF2A00]" data-testid="messages-error">
          {err}
        </div>
      )}
      {!err && !loading && filtered.length === 0 && (
        <div className="border border-black/10 p-8 text-center text-sm text-neutral-500" data-testid="messages-empty">
          {searchQuery ? `No matches for "${searchQuery}".` : "No messages to show for this filter."}
        </div>
      )}

      {/* List */}
      <div className="border border-black/10" data-testid="messages-list">
        {visible.map((row) => {
          const s = row.status || "new";
          const meta = STATUS_META[s] || STATUS_META.new;
          const isOpen = expandedId === row.id;
          return (
            <div key={row.id} className="border-b border-black/10 last:border-b-0" data-testid={`message-row-${row.id}`}>
              <button
                type="button"
                onClick={() => toggleExpand(row)}
                className="w-full grid grid-cols-12 gap-3 px-4 py-3 text-left hover:bg-neutral-50 items-center"
                aria-expanded={isOpen}
              >
                <div className="col-span-12 md:col-span-2">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 ${meta.className}`}
                    data-testid={`message-status-${row.id}`}
                  >
                    <meta.icon size={10} weight="bold" />
                    {meta.label}
                  </span>
                </div>
                <div className="col-span-12 md:col-span-3 min-w-0">
                  <div className="font-medium text-sm truncate"><Highlight text={row.name} query={searchQuery} /></div>
                  <div className="text-[11px] text-neutral-500 truncate"><Highlight text={row.email} query={searchQuery} /></div>
                </div>
                <div className="col-span-12 md:col-span-4 min-w-0">
                  <div className="text-sm truncate font-medium"><Highlight text={row.subject} query={searchQuery} /></div>
                  <div className="text-[11px] text-neutral-500 truncate"><Highlight text={row.category} query={searchQuery} /></div>
                </div>
                <div className="col-span-8 md:col-span-2 text-[11px] font-mono text-neutral-500">
                  {fmt(row.created_at)}
                </div>
                <div className="col-span-4 md:col-span-1 flex justify-end">
                  {isOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-2 bg-neutral-50 border-t border-black/10" data-testid={`message-body-${row.id}`}>
                  <div className="whitespace-pre-wrap text-sm text-neutral-800 leading-relaxed mb-4">
                    <Highlight text={row.message} query={searchQuery} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={replyLink(row)}
                      onClick={() => changeStatus(row.id, "replied")}
                      className="btn-primary text-[11px] px-3 py-1.5"
                      data-testid={`message-reply-${row.id}`}
                    >
                      <PaperPlaneTilt size={12} weight="bold" />
                      Reply via email
                    </a>
                    {s !== "read" && (
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => changeStatus(row.id, "read")}
                        className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border border-black/15 hover:bg-neutral-100"
                        data-testid={`message-mark-read-${row.id}`}
                      >
                        Mark read
                      </button>
                    )}
                    {s !== "replied" && (
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => changeStatus(row.id, "replied")}
                        className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border border-black/15 hover:bg-neutral-100"
                        data-testid={`message-mark-replied-${row.id}`}
                      >
                        Mark replied
                      </button>
                    )}
                    {s !== "archived" && (
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => changeStatus(row.id, "archived")}
                        className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border border-black/15 hover:bg-neutral-100 text-neutral-500"
                        data-testid={`message-archive-${row.id}`}
                      >
                        Archive
                      </button>
                    )}
                    {s === "archived" && (
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => changeStatus(row.id, "new")}
                        className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border border-black/15 hover:bg-neutral-100"
                        data-testid={`message-restore-${row.id}`}
                      >
                        Restore
                      </button>
                    )}
                    <span className="ml-auto text-[10px] font-mono text-neutral-400 hidden md:inline">
                      id: {row.id.slice(0, 8)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <Pagination
          totalItems={filtered.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          testidPrefix="messages-pagination"
        />
      </div>
    </div>
  );
}

function StatChip({ label, value, testid, accent }) {
  return (
    <div className="border border-black/10 p-3" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
      <div
        className="mt-1 font-heading font-black text-2xl tracking-tight tabular-nums"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
